"use client"

import { chakra, createSlotRecipeContext, HTMLChakraProps, mergeProps, RecipeVariantProps, useFieldContext } from "@chakra-ui/react"
import { forwardRef } from "react"
import { textareaRecipe } from "./textarea.recipe"

const { withProvider, withContext } = createSlotRecipeContext({
  recipe: textareaRecipe,
})

interface TextareaFieldProps extends HTMLChakraProps<"textarea", RecipeVariantProps<typeof textareaRecipe>> {
  placeholder?: string;
  value?: string | number;
  defaultValue?: string | number;
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  disabled?: boolean;
  required?: boolean;
  readOnly?: boolean;
  'aria-label'?: string;
  'aria-describedby'?: string;
  rows?: number;
  'data-e2e'?: string;
}
const BaseTextareaField = withProvider<HTMLTextAreaElement, TextareaFieldProps>("textarea", "field")

/**
 * @useFor multi-line text input inside a `<Field.Root>` — comments,
 *   notes, long descriptions. Defaults to 3 rows; pass `rows={n}` to
 *   override.
 *
 *   Examples in-tree: alert description, comment editor, runbook
 *   notes, role description.
 *
 *   For single-line input use `<InputField>`. For rich-text
 *   (formatting, attachments) use a different primitive — `<Textarea>`
 *   is plain text.
 */
export const TextareaField = forwardRef<HTMLTextAreaElement, TextareaFieldProps>(
  function TextareaField({ rows, ...props }, ref) {
    // Consume Field context the way Ark's Field.Textarea does,
    // so a sibling <Field.Label> associates without explicit htmlFor/id.
    // Optional outside <Field.Root>; explicit props win on conflict.
    const field = useFieldContext()
    // The context props are plain DOM textarea props — a strict subset of
    // the Chakra-styled prop surface — so widening them for the merge is safe.
    const fieldProps = (field?.getTextareaProps() ?? {}) as unknown as TextareaFieldProps
    const mergedProps = mergeProps(fieldProps, props)
    if (!mergedProps['aria-describedby'] && (mergedProps as any)['aria-errormessage']) {
      mergedProps['aria-describedby'] = (mergedProps as any)['aria-errormessage']
    }
    return <BaseTextareaField ref={ref} rows={rows ?? 3} {...mergedProps} />
  },
)

interface TextareaAddonProps extends HTMLChakraProps<"div"> {
  placement?: 'left' | 'right';
}
export const TextareaAddon = withContext<HTMLDivElement, TextareaAddonProps>("div", "addon")

interface TextareaElementProps extends HTMLChakraProps<"div"> {
  placement?: 'left' | 'right';
}
export const TextareaElement = withContext<HTMLDivElement, TextareaElementProps>("div", "element")

const BaseTextarea = chakra("textarea", textareaRecipe)

export const Textarea = ({ rows, ...props }: TextareaFieldProps) => {
  return <BaseTextarea rows={rows ?? 3} {...props} />
}
